import { IChecklistTask } from '@features/Checklist/model/types';

const CHECKLIST_BADGE_CLASSNAME = 'checklist-badge';
const getChecklistBadgeLabel = (labelPrefix: string, checked: number, overall: number) =>
    `${labelPrefix} ${checked}/${overall}`;
const getChecklistBadgeProgress = (checked: number, overall: number) =>
    overall > 0 ? Math.min(100, (checked / overall) * 100) : 0;

const isChecklistBadgeNodeSynced = ({
    badge,
    labelPrefix,
    checked,
    overall,
}: {
    badge: HTMLElement;
    labelPrefix: string;
    checked: number;
    overall: number;
}) => {
    const label = getChecklistBadgeLabel(labelPrefix, checked, overall);
    const progress = `${getChecklistBadgeProgress(checked, overall)}%`;
    const textNode = badge.querySelector<HTMLElement>('.checklist-badge__text');
    const overlayTextNode = badge.querySelector<HTMLElement>('.checklist-badge__overlay-text');

    return (
        badge.dataset.overall === String(overall) &&
        badge.dataset.checked === String(checked) &&
        badge.dataset.fullChecked === (checked >= overall ? 'true' : 'false') &&
        badge.style.getPropertyValue('--checklist-progress').trim() === progress &&
        textNode?.textContent === label &&
        overlayTextNode?.textContent === label
    );
};

const updateChecklistBadgeNode = ({
    badge,
    labelPrefix,
    checked,
    overall,
}: {
    badge: HTMLElement;
    labelPrefix: string;
    checked: number;
    overall: number;
}) => {
    const label = getChecklistBadgeLabel(labelPrefix, checked, overall);
    const progress = getChecklistBadgeProgress(checked, overall);

    badge.dataset.overall = String(overall);
    badge.dataset.checked = String(checked);
    badge.dataset.fullChecked = checked >= overall ? 'true' : 'false';
    badge.style.setProperty('--checklist-progress', `${progress}%`);

    const textNode = badge.querySelector<HTMLElement>('.checklist-badge__text');
    const overlayTextNode = badge.querySelector<HTMLElement>('.checklist-badge__overlay-text');

    if (textNode) {
        textNode.textContent = label;
    }

    if (overlayTextNode) {
        overlayTextNode.textContent = label;
    }
};

const createChecklistBadgeNode = (labelPrefix: string, checked: number, overall: number) => {
    const badge = document.createElement('span');
    const text = document.createElement('span');
    const overlay = document.createElement('span');
    const overlayText = document.createElement('span');

    badge.className = CHECKLIST_BADGE_CLASSNAME;
    text.className = 'checklist-badge__text';
    overlay.className = 'checklist-badge__overlay';
    overlayText.className = 'checklist-badge__overlay-text';

    overlay.append(overlayText);
    badge.append(text, overlay);

    updateChecklistBadgeNode({ badge, labelPrefix, checked, overall });

    return badge;
};

export const syncChecklistBadge = ({
    positionGuid,
    checklist,
    labelPrefix,
}: {
    positionGuid: string;
    checklist: IChecklistTask[] | null;
    labelPrefix: string;
}) => {
    if (!positionGuid || checklist === null) {
        return;
    }

    const rows = document.querySelectorAll<HTMLElement>(`[data-position-guid="${positionGuid}"]`);
    const overall = checklist.length;
    const checked = checklist.filter((item) => item.isChecked).length;

    rows.forEach((row) => {
        const existingBadge = row.querySelector<HTMLElement>(`.${CHECKLIST_BADGE_CLASSNAME}`);

        if (overall === 0) {
            existingBadge?.remove();
            return;
        }

        if (existingBadge) {
            if (
                isChecklistBadgeNodeSynced({
                    badge: existingBadge,
                    labelPrefix,
                    checked,
                    overall,
                })
            ) {
                return;
            }

            updateChecklistBadgeNode({ badge: existingBadge, labelPrefix, checked, overall });
            return;
        }

        const positionIcons = row.querySelector<HTMLElement>('.position-name-icons');
        const nameColumn = row.querySelector<HTMLElement>('[class*="name"]');
        const badge = createChecklistBadgeNode(labelPrefix, checked, overall);

        if (positionIcons) {
            positionIcons.insertAdjacentElement('afterend', badge);
            return;
        }

        nameColumn?.append(badge);
    });
};
